import { Button, TextArea, Icon, Loader, FileDrop, Progress } from '@scuf/common';
import React, { FunctionComponent, useState, useRef, useEffect } from 'react';
import { 
    postActionTrackingData, 
    } from 'src/services/UserAction';
import { useAppDispatch } from 'src/store';
import './CompleteTaskFileUpload.scss';
import { ReactComponent as RedTick } from 'src/images/red-tick.svg';
import { ReactComponent as YellowTick } from 'src/images/yellow-tick.svg';
import { ReactComponent as GreenTick } from 'src/images/green-tick.svg';
import {FILE_UPLOAD_FORMAT_ERROR,HAS_SCRIPT,FILE_UPLOAD_MANDATORY,FILE_UPLOAD_MANDATORY_COMMENT, USER_ROLE} from 'src/constants/constants';
import { Exception } from 'sass';
import moment from 'moment';
import { nanoid } from '@reduxjs/toolkit';

/** Display category for the reviewer-checklist action-owner tracking flow that supports training assignment */
const TRACKING_DISPLAY_CATEGORY = 'G&H Trends';
const TRAINING_LINK_ACTION_TYPE = 'acceptanddropdown';

interface ICompleteTaskFileUploadProps {
    action_tracking_id: string|Blob,
    status: string,
    org_owner_remarks: string,
    target_date : string,
    action_owner_comment: string,
    file_details: FileDetails[],
    postActionResponse : Function,
    handleTaskCheckbox: (status: boolean) => void;
    checkBoxStatus: boolean
    trainingOptions?: TrainingOption[]
    training_name?: string
    modelId?: number
    userRole?: string
    actionOwnerHid?: string
    handleReviewCheckBox?: Function
    handleFileDownload?: Function
    insightID?: number
    insightSummaryDisplayCategory?: string
    actionType?: string
    reasonId?: number | string
}

type TrainingOption = {
    training_id: number;
    training_name: string;
    training_link: string;
};

type FileDetails = {
    file_name: string;
    stored_file_name: string;
    activity_id: number
    created_on: string
};

type FileError = {
    error: boolean,
    errorText: string
}

interface INewFile {
    name?: string | undefined;
    id?: string;
    uploaded?: boolean;
}

export const CompleteTaskFileUpload: FunctionComponent<ICompleteTaskFileUploadProps> = (props) => {
    const [comments, setComments] = useState(props.action_owner_comment || '');
    const [uploadedFile, setUploadedFiles] = useState<File[]>([]);
    const [previousFiles, setPreviousFiles] = useState<FileDetails[]>(props.file_details);
    const [showRemarkError, setShowRemarkError] = useState(false);
    const [btnText, setBtnText] = useState('Save');
    const [previousStatus, setPreviousStatus] = useState(props.status);
    const dispatch = useAppDispatch();
    const [fileError, setFileError] = useState<FileError>({error: false, errorText: ''});
    const [errorMessage, setErrorMessage] = useState('');
    const [commentChanged, setCommentChanged] = useState(false);

    
    // const handleCommentsOnChange = (e: string) => {
    //     setComments(e);
    //     setCommentChanged(e !== props.action_owner_comment);
    //     props.handleTaskCheckbox(commentChanged)
    // };

    const handleCommentsOnChange = (e: string) => {
        setComments(e);
        setCommentChanged(false);
        if(e !== props.action_owner_comment){
            setCommentChanged(true);
        }
        if(props.action_owner_comment === null || previousFiles[0] ===  null) {
            setCommentChanged(false);
        }
        if(e && e !== props.action_owner_comment &&  previousFiles[0] !==  null) {
            props.handleTaskCheckbox(false);
        }
    };
    
    useEffect(() => {
       if((props.file_details && previousFiles.length !== props.file_details.length) ||
       previousStatus!== props.status ) {
            setUploadedFiles([]);
            setPreviousFiles(props.file_details);
            setPreviousStatus(props.status);
            setBtnText('Save');
       }
    }, [props.file_details, props.status]);


    const postActiontrackingData = () => {
        let inValidCharacter = /[<>()&#]/;
        setBtnText('Saving..');
        let formData = new FormData();
        formData.append(`supportFile`, uploadedFile[0] || null);
        formData.append('action_tracking_id', props.action_tracking_id);
        if(!inValidCharacter.test(comments)){
            setShowRemarkError(false);
            formData.append('remarks', comments);
            dispatch(postActionTrackingData(formData)).then(()=> {
            props.postActionResponse('success');
            setBtnText('Save');
            setUploadedFiles([]);
        }).catch((err: Exception)=> {
            props.postActionResponse('reject');
        })
      }else{
        setShowRemarkError(true);
        setBtnText('Save');
      }
      setCommentChanged(false);
    }

    const setFiles = (e: any) => {
        let fileList = e.target.files;
        const file = fileList[0];
        let extension = file.name.match(/\.([0-9a-z]+)(?:[\?#]|$)/i)[0].toLowerCase();
        extension = extension ? extension.substring(1) : '';
        let isAllowed = ['jpg', 'jpeg', 'pdf','docx', 'xlsx', 'png', 'msg', 'pst', 'ost', 'ns'].includes(extension);
        if (!isAllowed || ((file.size) > 20971520)) {
            setFileError({
                error: true,
                errorText: FILE_UPLOAD_FORMAT_ERROR
            });
        } else {
            setFileError({
                error: false,
                errorText: ''
            });
            setUploadedFiles([...fileList]);    
            file? props.handleTaskCheckbox(false) : props.handleTaskCheckbox(true)  
        }        
        setCommentChanged(false);
    }

    const removeFile = (e: any) => {
        setUploadedFiles([]);
        setFileError({
            error: false,
            errorText: ''
        });
    }

    const actionStatus = (status: string) => {
        let actionIcon = null;
       
        switch(status) {
            case 'red':
                actionIcon = <RedTick/>;
                break;
            case 'green':
                actionIcon = <GreenTick/>;
                break;
            case 'yellow':
                actionIcon = <YellowTick/>;
                break;
            default:
                actionIcon = null;
        }
        return (
            <div className='complete-task__action-status-icon'>
                {actionIcon}
            </div>
        )
    }

    const isPastDue = ()=> {
        const date1: any = new Date().toISOString().slice(0, 10);
        const todayDate: any = new Date(date1);
        const date2: any = new Date(props.target_date);
        const diffTime = (date2 - todayDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return diffDays < 0;
    }

    const displayError = () => {
        return (
            <div className='file-upload-error'>
                {fileError.error && <span>{fileError.errorText}</span>}
            </div>
        )
    }

    const endProgressSimulation = (uploadingFiles: File[]) => {
        setTimeout(() => {
            setUploadedFiles((allFiles: File[]) => {
            const updatedFiles = allFiles.map((file: INewFile) => {
                if (
                uploadingFiles
                    .map((loadingFile) => loadingFile.name)
                    .includes(file.name as string)
                ) {
                file.uploaded = true;
                return file;
                } else {
                return file;
                }
            });
            return updatedFiles as File[];
            });
        }, 2000);
    };

    const dropAccepted = (files: File[]) => {
        setUploadedFiles(
            (allFiles: File[]) => {
            const newFiles = files.map((file: INewFile) => {
                const fileInProgress = Object.assign(file, {
                uploaded: false,
                id: nanoid(),
                });
                return fileInProgress;
            });
            return [...newFiles];
            },
            // @ts-ignore
            endProgressSimulation(files)
        );
    };

    const isSaveEnabled = () => {
        const hasChanges = commentChanged || uploadedFile.length > 0;
        const isValid = comments.trim() !== "" && 
                       (uploadedFile.length > 0 || (previousFiles && previousFiles.length > 0));
            return hasChanges && isValid;
        };

    const matchedTraining = (props.trainingOptions || []).find(
        (option) => option.training_name === props.training_name
    );
    const isSafeTrainingLink = (link: string) => /^https:\/\//i.test(link);
    const isTrainingTrackingFlow = props.insightSummaryDisplayCategory === TRACKING_DISPLAY_CATEGORY;
    const isTrainingLinkReason = props.actionType === TRAINING_LINK_ACTION_TYPE;
    const shouldShowTrainingInfo = isTrainingTrackingFlow && isTrainingLinkReason;
        // const isTrainingTrackingFlow = true;

    const handleFileDownloadFunctionality = (stored_file_name: string, file_name: string) => {
        props.handleFileDownload && props.handleFileDownload(props.insightID, stored_file_name, file_name);
    }

    return (
        <div className='complete-task-file-upload-contianer'>
            <div className='complete-task-owner-container'>               
                <div className={`complete-task__comments`}>
                    {actionStatus(previousStatus)}
                    <div className={`complete-task__comment-title`}>Comments by org owner</div>
                    <div className='complete-task__comment-value'>{(props && props.org_owner_remarks)?? 'No Comments'}</div>
                    {shouldShowTrainingInfo && props.training_name && (
                        <div className='complete-task__training-name'>
                            <span className='complete-task__training-name-label'>Training Name: </span>
                            <span className='complete-task__training-name-value'>{props.training_name}</span>
                        </div>
                    )}
                    {shouldShowTrainingInfo && matchedTraining && isSafeTrainingLink(matchedTraining.training_link) && (
                        <div className='complete-task__training-link'>
                            <a href={matchedTraining.training_link} target="_blank" rel="noopener noreferrer">
                                Go to training
                            </a>
                        </div>
                    )}
                </div>
                <div className={`complete-task__target-date`}>
                    <div className='complete-task__target-date-title'>Target date</div>
                    <span className={`complete-task__target-date-value ${isPastDue() && 'past-due-task'}`}>{moment(props.target_date).format('MM/DD/YYYY')}</span>
                </div>
            </div>
            <div className='complete-task-remarks'>
                <TextArea
                    onChange={handleCommentsOnChange}
                    className="textarea-wrapper input-label-message textarea-fi disclaimer-comments"
                    fluid={false}
                    rows={2} 
                    indicator={'required'} 
                    placeholder={'Enter Your comments here'}
                    label="Add Comments"
                    value={comments.trimStart()}
                    required={true}
                /> 
            </div>
            <div className='{`complete-task__file-upload-container`}'>
                <FileDrop
                    acceptedTypes=".jpeg, .pdf,.jpg ,.docx,.xlsx,.png,.msg,.nst"
                    multiple={false}
                    maxSize={20000000}//(20MB)
                    placeholder="Drop or Upload File(.png | .jpg | .jpeg | .docx | .pdf |.xlsx|.msg|.nst)"
                    onChange={setFiles}
                    className="right-rail"
                    buttonText="Drag & drop or browse file"
                    onAccepted={(files) => {
                        dropAccepted(files);
                    }}
                    error={errorMessage}
                    disabled={uploadedFile.length > 0 ? true : false}
                    onRejected={([file]) => {
                        setUploadedFiles([]);
                        setErrorMessage('File Size of 20MB with following extension is supported .jpeg, .pdf, .jpg, .docx, .xlsx, .png, .msg, .nst please retry with a valid file')
                    }
                     }
                    onReset={() => { setErrorMessage(''); removeFile(''); }}
                ></FileDrop>
            </div>
            {commentChanged && comments.trim() !== "" ? <div className="changed_comment_file_note">{FILE_UPLOAD_MANDATORY_COMMENT}</div> : <div className="comment_file_note">{FILE_UPLOAD_MANDATORY}</div>}
            <div className="file-value-wrapper">
                {uploadedFile && uploadedFile.length > 0 && 
                    uploadedFile.map((file: INewFile) => (
                        <>
                            {file.uploaded ? (
                            <Icon name="check" color="green" />
                            ) : (
                            <Progress circular={true} size="xsmall" />
                            )}
                            <p className="truncate-text">{file.name}</p>
                        </>
                    ))
                }
            </div>
            {displayError()}
            {props && props.file_details?.length > 0 && <div className="file-value-wrapper old-files-wrapper">
                {props.file_details.map((file: FileDetails, index: any) =>(
                    file && <span 
                                className="file-value-wrapper-content"
                                key={`${index}-${file.file_name}`}>
                                    <Icon 
                                        name="document-report" 
                                        root="common"
                                        size="small"
                                        className='file-value-icon'/>
                                    <div className='file-value-wrapper-content-data'> {` ${file.file_name}`}</div>
                            </span>))}
            </div>}
            <div className='complete-task-button'>
                <Button
                    className="button--secondary notes-cancel button-space"
                    size="small"
                    type="secondary"
                    disabled={!isSaveEnabled()}
                    onClick={postActiontrackingData}
                    content={btnText}
                /> 
            </div>
            {showRemarkError && (
                    <div className="completetask__footer--err">{HAS_SCRIPT}</div>
            )}
            {isTrainingTrackingFlow && (
                <>
                    <div className='review__completion--comments'>
                        <span className='review__completion--commentheader'>Comments Given by Action Owner: </span>
                        <span className='review__completion--commentscontent'>{props.action_owner_comment}</span>
                    </div>
                    <div className='review__completion--attachments'>
                        <span className='review__completion--commentheader'>Attachments from Action Owner: </span>
                        <span className='review__completion--attachmentcontent'>
                            {props.file_details && props.file_details.length > 0 && props.file_details.map((item: FileDetails, index: number) => {
                                if (item) {
                                    return (
                                        <div key={`review-attachment-${index}`}>
                                            <span className='truncate-text'>{item.file_name}</span>
                                            <Icon
                                                root="common"
                                                color="blue"
                                                name="cloud-download"
                                                size="small"
                                                className="download_icon"
                                                onClick={() => handleFileDownloadFunctionality(item.stored_file_name, item.file_name)}
                                            />
                                        </div>
                                    );
                                }
                                return null;
                            })}
                        </span>
                    </div>
                    <div className='review__completion--comments'>
                        <span className='review__completion--commentheader'>Comments Given by Org Owner:</span>
                        <span className='review__completion--commentscontent'>{props.org_owner_remarks}</span>
                    </div>
                </>
            )}
        </div>
    );
};